/**
 * Default layout for the Acquisition page.
 *
 * Matches the Urban Unity dashboard composition:
 * - 3 MetricCards (Cash Collected, Revenue, Close Rate)
 * - 4 StatCards (Show Rate, Calls Due, Calls Taken, Calls Closed)
 * - 4 StatCards (Avg Cash/Call, Cash/Close, Cash/Application, Cash/Opt-in)
 * - 2 StatCards (Total Cash Applications, Total Cash Opt-ins)
 * - 5 AdditionalStats (New Cash, MRR, Gross Revenue, Total Applications, Total Opt-ins)
 * - 3 full-width pieCharts (Country, Source, Deal Status)
 * - 1 table (Recent Calls)
 */

import type { DashboardLayout } from "../types";

export const ACQUISITION_DEFAULT: DashboardLayout = {
  widgets: [
    // --- Row 1: Metric Cards (y:0) ---
    {
      id: "acq-metric-cash",
      type: "metric",
      title: "Cash Collected",
      config: {
        metricKey: "totalCashCollected",
        format: "currency",
        icon: "dollar",
        color: "brand",
      },
    },
    {
      id: "acq-metric-revenue",
      type: "metric",
      title: "Revenue",
      config: {
        metricKey: "totalRevenue",
        format: "currency",
        icon: "trending",
        color: "green",
      },
    },
    {
      id: "acq-metric-closerate",
      type: "metric",
      title: "Close Rate",
      config: {
        metricKey: "closeRate",
        format: "percent",
        icon: "chart",
        color: "blue",
      },
    },

    // --- Row 2: Call Stat Cards (y:3) ---
    {
      id: "acq-stat-showrate",
      type: "stat",
      title: "Show Rate",
      config: { metricKey: "showRate", format: "percent" },
    },
    {
      id: "acq-stat-callsdue",
      type: "stat",
      title: "Calls Due",
      config: { metricKey: "callsDue", format: "number" },
    },
    {
      id: "acq-stat-callstaken",
      type: "stat",
      title: "Calls Taken",
      config: { metricKey: "callsTaken", format: "number" },
    },
    {
      id: "acq-stat-callsclosed",
      type: "stat",
      title: "Calls Closed",
      config: { metricKey: "callsClosed", format: "number" },
    },

    // --- Row 3: Average Stat Cards (y:5) ---
    {
      id: "acq-stat-avgcashcall",
      type: "stat",
      title: "Avg. Cash/Call",
      config: { metricKey: "avgCashPerCall", format: "currency" },
    },
    {
      id: "acq-stat-avgcashclose",
      type: "stat",
      title: "Avg. Cash/Close",
      config: { metricKey: "avgCashPerClose", format: "currency" },
    },
    {
      id: "acq-stat-avgcashapp",
      type: "stat",
      title: "Avg. Cash/Application",
      config: { metricKey: "avgCashPerApplication", format: "currency" },
    },
    {
      id: "acq-stat-avgcashoptin",
      type: "stat",
      title: "Avg. Cash/Opt-in",
      config: { metricKey: "avgCashPerOptin", format: "currency" },
    },

    // --- Row 4: Total Cash Breakdown (y:7) ---
    {
      id: "acq-stat-totalcashapp",
      type: "stat",
      title: "Total Cash (Applications)",
      config: { metricKey: "totalApplicationsCash", format: "currency" },
    },
    {
      id: "acq-stat-totalcashoptin",
      type: "stat",
      title: "Total Cash (Opt-ins)",
      config: { metricKey: "totalOptinsCash", format: "currency" },
    },

    // --- Row 5: Additional Stats (y:9) ---
    {
      id: "acq-addstat-newcash",
      type: "additionalStat",
      title: "New Cash Collected",
      config: {
        metricKey: "newCashCollected",
        format: "currency",
        description: "Total cash from new deals (excludes MRR)",
      },
    },
    {
      id: "acq-addstat-mrr",
      type: "additionalStat",
      title: "MRR",
      config: {
        metricKey: "mrrCash",
        format: "currency",
        description: "Monthly recurring revenue",
      },
    },
    {
      id: "acq-addstat-grossrev",
      type: "additionalStat",
      title: "Total Gross Revenue",
      config: {
        metricKey: "totalGrossRevenue",
        format: "currency",
        description: "Total contract value (all revenue)",
      },
    },
    {
      id: "acq-addstat-totalapps",
      type: "additionalStat",
      title: "Total Applications",
      config: {
        metricKey: "totalApplications",
        format: "number",
      },
    },
    {
      id: "acq-addstat-totaloptins",
      type: "additionalStat",
      title: "Total Opt-ins",
      config: {
        metricKey: "totalOptins",
        format: "number",
      },
    },
    // --- Charts ---
    {
      id: "acq-pie-country",
      type: "pieChart",
      title: "Closes by Country",
      config: {
        dataKey: "countryData",
        titleClosed: "Closes by Country",
        titleAll: "Leads by Country",
        leadModeAware: true,
      },
    },
    {
      id: "acq-pie-source",
      type: "pieChart",
      title: "Closes by Source",
      config: {
        dataKey: "leadSourceData",
        titleClosed: "Closes by Source",
        titleAll: "Leads by Source",
        leadModeAware: true,
      },
    },
    {
      id: "acq-pie-dealstatus",
      type: "pieChart",
      title: "Deal Status Breakdown",
      config: { dataKey: "outcomeBreakdown", title: "Deal Status Breakdown" },
    },
    {
      id: "acq-table-recentcalls",
      type: "table",
      title: "Recent Calls",
      config: {
        dataKey: "recentCalls",
        title: "Recent Calls",
        search: {
          enabled: true,
          placeholder: "Search lead name, email, phone...",
          keys: ["leadName", "email", "phone"],
        },
        filters: [
          { key: "status", label: "Status" },
          { key: "closer", label: "Closer" },
          { key: "setter", label: "Setter" },
          { key: "callOutcome", label: "Call Outcome" },
        ],
      },
    },
  ],

  gridLayout: [
    // Row 1: 3 metric cards (h=3)
    { i: "acq-metric-cash", x: 0, y: 0, w: 4, h: 3 },
    { i: "acq-metric-revenue", x: 4, y: 0, w: 4, h: 3 },
    { i: "acq-metric-closerate", x: 8, y: 0, w: 4, h: 3 },

    // Row 2: 4 stat cards (h=2)
    { i: "acq-stat-showrate", x: 0, y: 3, w: 3, h: 2 },
    { i: "acq-stat-callsdue", x: 3, y: 3, w: 3, h: 2 },
    { i: "acq-stat-callstaken", x: 6, y: 3, w: 3, h: 2 },
    { i: "acq-stat-callsclosed", x: 9, y: 3, w: 3, h: 2 },

    // Row 3: 4 stat cards — averages (h=2)
    { i: "acq-stat-avgcashcall", x: 0, y: 5, w: 3, h: 2 },
    { i: "acq-stat-avgcashclose", x: 3, y: 5, w: 3, h: 2 },
    { i: "acq-stat-avgcashapp", x: 6, y: 5, w: 3, h: 2 },
    { i: "acq-stat-avgcashoptin", x: 9, y: 5, w: 3, h: 2 },

    // Row 4: 2 stat cards — total cash breakdown (h=2)
    { i: "acq-stat-totalcashapp", x: 0, y: 7, w: 6, h: 2 },
    { i: "acq-stat-totalcashoptin", x: 6, y: 7, w: 6, h: 2 },

    // Row 5: 5 additional stats (h=3)
    { i: "acq-addstat-newcash", x: 0, y: 9, w: 2, h: 3 },
    { i: "acq-addstat-mrr", x: 2, y: 9, w: 2, h: 3 },
    { i: "acq-addstat-grossrev", x: 4, y: 9, w: 2, h: 3 },
    { i: "acq-addstat-totalapps", x: 6, y: 9, w: 3, h: 3 },
    { i: "acq-addstat-totaloptins", x: 9, y: 9, w: 3, h: 3 },

    // Row 6: full-width pie chart (Country)
    { i: "acq-pie-country", x: 0, y: 12, w: 12, h: 14 },

    // Row 7: full-width pie chart (Source)
    { i: "acq-pie-source", x: 0, y: 26, w: 12, h: 14 },

    // Row 8: full-width pie chart (Deal Status)
    { i: "acq-pie-dealstatus", x: 0, y: 40, w: 12, h: 14 },

    // Row 9: table
    { i: "acq-table-recentcalls", x: 0, y: 54, w: 12, h: 16 },
  ],
};
